import React from 'react'
import { API, Auth } from 'aws-amplify';
import "@aws-amplify/ui-react/styles.css";
import Loadingmini from '../components/loadingmini';
import { Flex, View, Text, Button, Heading } from '@aws-amplify/ui-react';
import { api3Post } from '../api/api3Client';

const DRIVER_TIMEZONE = 'America/Chicago';

function money(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  if (Number.isNaN(number)) return null;
  return `$${number.toFixed(2)}`;
}

function errorText(err) {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err.message) return err.message;
  try {
    return JSON.stringify(err);
  } catch (e) {
    return 'Unknown error';
  }
}

function rentalExplain(data) {
  const inputs = data && data.rental_inputs;
  if (!inputs || !inputs.driver_applied) return null;
  const parts = [
    `Driving behavior ${inputs.driving_behavior_score}, trust ${inputs.trust_score}, payment risk ${inputs.payment_risk_score}.`
  ];
  if (inputs.driver_discount !== null && inputs.driver_discount !== undefined && Number(inputs.driver_discount) !== 0) {
    parts.push(`Driver discount ${money(inputs.driver_discount)} per day.`);
  }
  return parts.join(' ');
}

function creditNote(pricing) {
  if (!pricing) return '';
  const parts = [];
  const gig = Number(pricing.oneTimeCreditUsd || 0);
  const first = Number(pricing.firstTimeDiscountUsd || 0);
  if (gig > 0) parts.push(`Includes a ${money(gig)} credit for connected work accounts.`);
  if (first > 0) parts.push(`Includes a ${money(first)} first-time discount.`);
  return parts.join(' ');
}

export default function RateCheck({ carId, driverId, fleetCode }) {
  const [isLoading, updateIsLoading] = React.useState(false);
  const [myerror, updateMyerror] = React.useState(false);
  const [showResult, updateShowResult] = React.useState(false);
  const [showOptions, updateShowOptions] = React.useState(false);
  const [lengthDescriptor, updateLengthDescriptor] = React.useState('');
  const [rentalRows, updateRentalRows] = React.useState([]);
  const [insurance, updateInsurance] = React.useState(null);

  const hasCar = carId !== null && carId !== undefined && Number(carId) > 0;
  const hasDriver = driverId !== null && driverId !== undefined && Number(driverId) > 0;

  async function priceCall(extra) {
    const session = await Auth.currentSession();
    const jwt = session.getAccessToken().getJwtToken();
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + extra.days);
    const query = {
      back_end: process.env.REACT_APP_BACKEND,
      jwt: jwt,
      api_endpoint: 'api3:staff:reservation_price',
      res_start: start.toISOString(),
      res_end: end.toISOString(),
    };
    if (extra.carId) query.car_id = extra.carId;
    if (extra.driverId) query.driver_id = extra.driverId;
    if (extra.fleet) query.fleet = extra.fleet;
    if (extra.timezone) query.timezone = extra.timezone;
    return API.get('adminWebTools', '/ago', { queryStringParameters: query });
  }

  async function loadRentalRow(label, extra) {
    try {
      const data = await priceCall(extra);
      if (data && data.status && data.status !== 'success' && data.gross_price === undefined) {
        return { label, error: data.message || data.status };
      }
      return { label, data, explain: rentalExplain(data) };
    } catch (e) {
      return { label, error: errorText(e) };
    }
  }

  async function loadInsurance(days) {
    const body = { rentalDays: days };
    if (fleetCode) body.fleetCode = fleetCode;
    if (hasDriver) body.driverId = Number(driverId);
    if (hasCar) body.carId = Number(carId);
    try {
      return { data: await api3Post('/insurance/programs/quote', body) };
    } catch (e) {
      return { error: errorText(e) };
    }
  }

  async function checkPricing(days) {
    updateIsLoading(true);
    updateMyerror(false);
    const jobs = [];
    if (hasCar) {
      jobs.push(loadRentalRow('This vehicle, standard driver', { days, carId }));
    }
    if (hasDriver) {
      jobs.push(loadRentalRow('This driver, base vehicle', {
        days,
        driverId,
        fleet: fleetCode,
        timezone: DRIVER_TIMEZONE,
      }));
    }
    if (hasCar && hasDriver) {
      jobs.push(loadRentalRow('This driver on this vehicle', { days, carId, driverId }));
    }
    try {
      const [rows, quote] = await Promise.all([
        Promise.all(jobs),
        loadInsurance(days),
      ]);
      updateRentalRows(rows);
      updateInsurance(quote);
      updateShowResult(true);
    } catch (e) {
      updateMyerror(errorText(e));
    }
    updateIsLoading(false);
  }

  function checkWeekly() {
    updateLengthDescriptor('Weekly rate');
    checkPricing(7);
  }

  function checkDaily() {
    updateLengthDescriptor('Daily rate');
    checkPricing(1);
  }

  const offering = insurance && insurance.data && insurance.data.quoteTrace
    ? insurance.data.quoteTrace.insuranceOffering
    : null;
  const preferred = insurance && insurance.data && insurance.data.quoteTrace
    ? insurance.data.quoteTrace.preferredInsurancePath
    : null;
  const explain = insurance && insurance.data && insurance.data.quoteTrace
    && insurance.data.quoteTrace.ratingContext
    ? insurance.data.quoteTrace.ratingContext.driverExplain
    : null;
  const items = insurance && insurance.data && Array.isArray(insurance.data.items)
    ? insurance.data.items
    : [];

  return (
    isLoading ? <Loadingmini /> :
    <Flex direction="column">
      <View style={{width:'100%'}}>
        { myerror ?
          <View>
            <Heading level={4}>Whoops!</Heading>
            <Text>{typeof myerror === 'string' ? myerror : JSON.stringify(myerror)}</Text>
            <Button onClick={() => updateShowOptions(false)}>BACK</Button>
          </View>
        : showResult ?
          <View>
            <Heading level={2}>Rates</Heading>
            <Heading level={4}>{lengthDescriptor}</Heading>
            <Heading level={3}>Rental</Heading>
            <Text>Rental prices are before tax. Tax is shown only when the quote is for a vehicle.</Text>
            {rentalRows.map((row) => (
              <View key={row.label} style={{marginTop:'0.75rem'}}>
                <Text><strong>{row.label}</strong></Text>
                {row.error ?
                  <Text>{row.error}</Text>
                :
                  <View>
                    <Text>Rental: {money(row.data && row.data.gross_price) || '—'}</Text>
                    {row.data && row.data.price_taxes_summed !== undefined ?
                      <View>
                        <Text>Tax: {money(row.data.price_taxes_summed) || '—'}</Text>
                        <Text>Total with tax: {money(row.data.price_total) || '—'}</Text>
                      </View>
                    :
                      <Text>Tax is not included.</Text>
                    }
                    {row.explain ? <Text>{row.explain}</Text> : null}
                  </View>
                }
              </View>
            ))}

            <Heading level={3}>Insurance</Heading>
            {hasDriver && !hasCar ?
              <Text>Rated without a vehicle. Some programs only apply once a car is known.</Text>
            : null}
            {explain && explain.message ? <Text>{explain.message}</Text> : null}
            {insurance && insurance.error ? <Text>{insurance.error}</Text> : null}
            {offering && offering.fleetProvidesCoverage ?
              <Text>Fleet coverage. This fleet provides coverage, so there is no separate driver insurance premium.</Text>
            : null}
            {offering && offering.allowPersonal ?
              <View style={{marginTop:'0.5rem'}}>
                <Text><strong>Own insurance</strong> {money(0)}</Text>
                <Text>{preferred === 'personal' ? 'This driver is on this path.' : 'Allowed on this fleet.'}</Text>
              </View>
            : null}
            {preferred === 'ago' ?
              <Text>This driver is on purchased coverage.</Text>
            : null}
            {items.map((item) => {
              const pricing = item.pricing || {};
              const total = money(pricing.totalDueUsd !== undefined ? pricing.totalDueUsd : pricing.insurancePremiumUsd);
              const perDay = money(pricing.perRentalDayUsd);
              const note = creditNote(pricing);
              return (
                <View key={`${item.programMasterId}-${item.variantId}`} style={{marginTop:'0.5rem'}}>
                  <Text><strong>{item.displayName || item.variantCode || 'Coverage'}</strong> {total || '—'}</Text>
                  {perDay ? <Text>{perDay} per day for {pricing.rentalDays} days.</Text> : null}
                  {note ? <Text>{note}</Text> : null}
                  {pricing.precision && pricing.precision !== 'exact' && pricing.message ?
                    <Text>{pricing.message}</Text>
                  : null}
                </View>
              );
            })}
            {!insurance?.error && items.length === 0 && !(offering && (offering.allowPersonal || offering.fleetProvidesCoverage)) ?
              <Text>No insurance choices for this fleet.</Text>
            : null}
            <br/>
            <Button onClick={() => updateShowResult(false)}>CLOSE</Button>
          </View>
        : showOptions ?
          <View>
            <Heading level={2}>Rates</Heading>
            <br/>
            <Button onClick={() => checkWeekly()}>CHECK WEEKLY RATE</Button>
            <br/>
            <br/>
            <Button onClick={() => checkDaily()}>CHECK DAILY RATE</Button>
            <br/>
            <br/>
            <Button onClick={() => updateShowOptions(false)}>CANCEL</Button>
          </View>
        :
          <Button onClick={() => updateShowOptions(true)}>
            CHECK RATES
          </Button>
        }
      </View>
    </Flex>
  );
}
